/**
 * Generic control renderer component that handles rendering different control types
 * based on the slot configuration from layout strategies.
 *
 * Uses unified LayoutConfigContext for all layout modes. The context handles
 * layout-specific logic internally (compare mode uses columnIndex, others ignore it).
 */

import React from 'react';

import {
  AxisRangeControl,
  CaptionControl,
  CompareEntryControl,
  IndexControl,
  MediaKeyControl,
  Pagination,
  RunControl,
  StepControl,
} from '../../controls';
import {
  getActiveAxes,
  useIndexSliderKeyboardBindings,
  useStepSliderKeyboardBindings,
} from '../utils/slotUtils';
import {type ControlRendererProps} from './types';

/**
 * Renders a single control based on its type and configuration.
 *
 * @param props - Control renderer props
 * @returns Rendered control or null if hidden/invalid
 */
export const ControlRenderer: React.FC<ControlRendererProps> = ({
  control,
  index,
  config,
  updateConfig,
  stepMeta,
  columnIndex,
  rowIndex,
  disableRunLinks,
  isMaxStepLoading,
  mediaBrowserSize,
  contentFooterWidth,
  gridData,
  layout,
  isFullScreenPanelPage = false,
  tilePagination,
  panelViewerRef,
}) => {
  const stepSliderKeyboardBindings = useStepSliderKeyboardBindings();
  const indexSliderKeyboardBindings = useIndexSliderKeyboardBindings();
  const activeAxes = getActiveAxes(config);

  // Individual control components handle their own label rendering
  // ControlRenderer just delegates to the appropriate component
  switch (control.control) {
    case 'step': {
      return (
        <StepControl
          key={`step-${index}`}
          control={control}
          index={index}
          config={config}
          stepMeta={stepMeta}
          columnIndex={columnIndex}
          rowIndex={rowIndex}
          stepSliderKeyboardBindings={stepSliderKeyboardBindings}
          activeAxes={activeAxes}
          layout={layout}
        />
      );
    }

    case 'index': {
      return (
        <IndexControl
          key={`index-${index}`}
          control={control}
          index={index}
          config={config}
          stepMeta={stepMeta}
          columnIndex={columnIndex}
          rowIndex={rowIndex}
          indexSliderKeyboardBindings={indexSliderKeyboardBindings}
          activeAxes={activeAxes}
          layout={layout}
        />
      );
    }

    case 'xAxisRange': {
      // Only show if we have the required data
      const showXAxis =
        config.gridSettings?.xAxis === 'step' &&
        gridData?.metadata.xAxisTicks != null &&
        gridData?.metadata.allXAxisValues != null &&
        (config.columnCount ?? 1) > 1 &&
        !isMaxStepLoading;

      if (!showXAxis || !gridData) {
        return null;
      }

      return (
        <AxisRangeControl
          key={`xAxisRange-${index}`}
          axis="xAxis"
          axisLabel="Steps"
          gridData={gridData}
          config={config}
          width={contentFooterWidth}
          updateConfig={updateConfig}
          direction="bottom"
          isMaxStepLoading={isMaxStepLoading}
        />
      );
    }

    case 'yAxisRange': {
      // Only show if we have the required data
      const showYAxis =
        config.gridSettings?.yAxis != null &&
        gridData?.metadata.yAxisTicks != null &&
        gridData?.metadata.allYAxisValues != null &&
        !isMaxStepLoading;

      if (!showYAxis || !gridData) {
        return null;
      }

      return (
        <AxisRangeControl
          key={`yAxisRange-${index}`}
          axis="yAxis"
          axisLabel={config.gridSettings?.yAxis === 'step' ? 'Steps' : 'Runs'}
          gridData={gridData}
          config={config}
          width={contentFooterWidth}
          updateConfig={updateConfig}
          direction="left"
          isMaxStepLoading={isMaxStepLoading}
        />
      );
    }

    case 'run': {
      // Show color pill only in compare mode AND fullscreen (modal)
      // In workspace, always use colorized run name
      const showColorPill = config.mode === 'compare' && isFullScreenPanelPage;
      return (
        <RunControl
          key={`run-${index}`}
          control={control}
          index={index}
          config={config}
          columnIndex={columnIndex}
          rowIndex={rowIndex}
          disableRunLinks={disableRunLinks}
          showColorPill={showColorPill}
        />
      );
    }

    case 'mediaKey': {
      return (
        <MediaKeyControl
          key={`mediaKey-${index}`}
          control={control}
          index={index}
          config={config}
          columnIndex={columnIndex}
        />
      );
    }
    case 'caption': {
      return (
        <CaptionControl
          key={`caption-${index}`}
          control={control}
          index={index}
          config={config}
          columnIndex={columnIndex}
          rowIndex={rowIndex}
        />
      );
    }

    case 'compareEntry': {
      return (
        <CompareEntryControl
          key={`compare-entry-${index}`}
          config={config}
          isFullScreenPanelPage={isFullScreenPanelPage}
          updateConfig={updateConfig}
        />
      );
    }

    case 'pagination': {
      // Only render if we have pagination data and a ref
      if (!tilePagination || !panelViewerRef) {
        return null;
      }
      return (
        <Pagination
          key={`pagination-${index}`}
          tilePagination={tilePagination}
          updateConfig={updateConfig}
          panelViewerRef={panelViewerRef}
        />
      );
    }

    default:
      return null;
  }
};
